import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { isValidElement, useMemo, type ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

const sanitizeSchema = {
    ...defaultSchema,
    tagNames: [
        'a', 'b', 'blockquote', 'br', 'code', 'del', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'hr', 'i', 'img', 'ins', 'li', 'mark', 'ol', 'p', 'pre', 's', 'strong', 'sub', 'sup',
        'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'ul'
    ],
    attributes: {
        ...defaultSchema.attributes,
        a: ['href', 'title', 'target', 'rel'],
        code: ['className'],
        img: ['src', 'alt', 'title'],
        th: ['align'],
        td: ['align']
    },
    protocols: {
        href: ['http', 'https', 'mailto'],
        src: ['http', 'https']
    }
};

function CodeBlock({ value }: { value: string }) {
    const theme = useTheme();
    return (
        <Box
            component="pre"
            sx={{
                m: '8px 0',
                p: 1.5,
                bgcolor: (t) => t.palette.action.hover,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflowX: 'auto',
                fontSize: 12.5,
                lineHeight: 1.5,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                color: theme.palette.text.primary
            }}
        >
            <code>{value}</code>
        </Box>
    );
}

function extractText(node: ReactNode): string {
    if (node == null || typeof node === 'boolean') {
        return '';
    }
    if (typeof node === 'string' || typeof node === 'number') {
        return String(node);
    }
    if (Array.isArray(node)) {
        return node.map(extractText).join('');
    }
    if (isValidElement(node)) {
        return extractText((node as { props: { children?: ReactNode } }).props.children);
    }
    return '';
}

function createMarkdownComponents(): Components {
    return {
        p: ({ children }) => (
            <Typography component="p" sx={{ m: '4px 0', fontSize: 14, lineHeight: 1.6 }}>
                {children}
            </Typography>
        ),
        h1: ({ children }) => (
            <Typography component="h1" sx={{ m: '12px 0 6px', fontSize: 20, fontWeight: 600 }}>
                {children}
            </Typography>
        ),
        h2: ({ children }) => (
            <Typography component="h2" sx={{ m: '12px 0 6px', fontSize: 18, fontWeight: 600 }}>
                {children}
            </Typography>
        ),
        h3: ({ children }) => (
            <Typography component="h3" sx={{ m: '10px 0 4px', fontSize: 16, fontWeight: 600 }}>
                {children}
            </Typography>
        ),
        ul: ({ children }) => (
            <Box component="ul" sx={{ m: '4px 0', pl: 2.75 }}>
                {children}
            </Box>
        ),
        ol: ({ children }) => (
            <Box component="ol" sx={{ m: '4px 0', pl: 2.75 }}>
                {children}
            </Box>
        ),
        li: ({ children }) => (
            <Box component="li" sx={{ m: '2px 0', fontSize: 14 }}>
                {children}
            </Box>
        ),
        pre: ({ children }) => {
            const value = extractText(children).replace(/\n$/, '');
            return <CodeBlock value={value} />;
        },
        code: ({ children }) => (
            <Box
                component="code"
                sx={{
                    px: 0.75,
                    py: 0.125,
                    bgcolor: 'action.selected',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 0.5,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: '0.88em'
                }}
            >
                {children}
            </Box>
        ),
        a: ({ children, href }) => (
            <Box component="a" href={href} target="_blank" rel="noopener noreferrer" sx={{ color: 'info.main', textDecoration: 'none' }}>
                {children}
            </Box>
        ),
        blockquote: ({ children }) => (
            <Box component="blockquote" sx={{ m: '8px 0', pl: 1.5, borderLeft: '3px solid', borderColor: 'divider', color: 'text.secondary' }}>
                {children}
            </Box>
        ),
        table: ({ children }) => (
            <Box sx={{ overflowX: 'auto', my: 1 }}>
                <Box
                    component="table"
                    sx={{
                        borderCollapse: 'collapse',
                        width: '100%',
                        fontSize: '0.9em'
                    }}
                >
                    {children}
                </Box>
            </Box>
        ),
        th: ({ children }) => (
            <Box
                component="th"
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    px: 1,
                    py: 0.75,
                    textAlign: 'left',
                    fontWeight: 600,
                    bgcolor: 'action.hover'
                }}
            >
                {children}
            </Box>
        ),
        td: ({ children }) => (
            <Box
                component="td"
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    px: 1,
                    py: 0.75,
                    verticalAlign: 'top'
                }}
            >
                {children}
            </Box>
        )
    };
}

export type EfMarkdownContentProps = {
    content: string;
};

export function EfMarkdownContent({ content }: EfMarkdownContentProps) {
    const components = useMemo(() => createMarkdownComponents(), []);
    return (
        <Box sx={{ fontSize: 14, color: 'text.primary', lineHeight: 1.6, overflowWrap: 'break-word' }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]} components={components}>
                {content}
            </ReactMarkdown>
        </Box>
    );
}
